<template>
  <div class="org-chart-node" :data-emp-norm="node.empIdNorm">
    <div
      class="org-chart-card"
      :class="{
        'org-chart-card--highlight': highlighted,
        'org-chart-card--selected': selected,
        'org-chart-card--inactive': !isActive,
        'org-chart-card--has-children': hasChildren,
        'org-chart-card--expanded': hasChildren && !collapsed,
      }"
      role="button"
      tabindex="0"
      :aria-expanded="hasChildren ? (!collapsed).toString() : undefined"
      :aria-label="cardAriaLabel"
      @click="onCardActivate"
      @keydown.enter.prevent="onCardActivate"
      @keydown.space.prevent="onCardActivate"
    >
      <div class="org-chart-card__accent" aria-hidden="true" />
      <b-avatar
        size="44"
        :src="avatarUrl || undefined"
        :text="initials"
        variant="light-primary"
        class="org-chart-card__avatar"
      />
      <div class="org-chart-card__body">
        <div class="org-chart-card__name" :title="name">
          {{ name }}
        </div>
        <div
          v-if="designation"
          class="org-chart-card__designation"
          :title="designation"
        >
          {{ designation }}
        </div>
        <div
          v-if="department && department !== designation"
          class="org-chart-card__dept"
          :title="department"
        >
          {{ department }}
        </div>
      </div>
      <div class="org-chart-card__actions">
        <button
          type="button"
          class="org-chart-card__info"
          title="Employee details"
          aria-label="Employee details"
          @click.stop="$emit('select', node)"
        >
          <feather-icon icon="InfoIcon" size="14" class="org-chart-card__info-icon" />
        </button>
        <button
          v-if="hasChildren"
          type="button"
          class="org-chart-card__toggle"
          :title="collapsed ? `Show ${node.children.length} report${node.children.length === 1 ? '' : 's'}` : `Hide ${node.children.length} report${node.children.length === 1 ? '' : 's'}`"
          :aria-expanded="(!collapsed).toString()"
          :aria-label="collapsed ? `Expand ${node.children.length} reports` : `Collapse ${node.children.length} reports`"
          @click.stop="$emit('toggle', node.empIdNorm)"
        >
          <feather-icon
            :icon="collapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
            size="16"
            class="org-chart-card__chevron"
          />
          <span class="org-chart-card__count">{{ node.children.length }}</span>
        </button>
      </div>
    </div>

    <transition name="org-children">
      <div v-if="hasChildren && !collapsed" class="org-chart-children">
        <div class="org-chart-children__connector" aria-hidden="true" />
        <div
          class="org-chart-children__row"
          :class="{ 'org-chart-children__row--multi': node.children.length > 1 }"
        >
          <div
            v-for="child in node.children"
            :key="child.empIdNorm"
            class="org-chart-children__item"
          >
            <org-chart-node
              :node="child"
              :collapsed-set="collapsedSet"
              :highlight-set="highlightSet"
              :selected-norm="selectedNorm"
              @toggle="$emit('toggle', $event)"
              @select="$emit('select', $event)"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { BAvatar } from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'
import {
  displayName,
  displayDesignation,
  displayDepartment,
  displayAvatar,
  isActiveUser,
} from '@/utils/org-chart-tree'

export default {
  name: 'OrgChartNode',
  components: {
    BAvatar,
  },
  props: {
    node: {
      type: Object,
      required: true,
    },
    collapsedSet: {
      required: true,
      validator: v => v instanceof Set || (v && typeof v.has === 'function'),
    },
    highlightSet: {
      default: () => new Set(),
      validator: v => v instanceof Set || (v && typeof v.has === 'function'),
    },
    selectedNorm: {
      type: String,
      default: null,
    },
  },
  computed: {
    name() {
      return displayName(this.node.user)
    },
    designation() {
      return displayDesignation(this.node.user)
    },
    department() {
      return displayDepartment(this.node.user)
    },
    avatarUrl() {
      return displayAvatar(this.node.user)
    },
    initials() {
      return avatarText(this.name)
    },
    isActive() {
      return isActiveUser(this.node.user)
    },
    hasChildren() {
      return this.node.children && this.node.children.length > 0
    },
    collapsed() {
      return this.collapsedSet.has(this.node.empIdNorm)
    },
    highlighted() {
      return this.highlightSet.has(this.node.empIdNorm)
    },
    selected() {
      return !!this.selectedNorm && this.selectedNorm === this.node.empIdNorm
    },
    cardAriaLabel() {
      const parts = [this.name]
      if (this.designation) parts.push(this.designation)
      if (this.department && this.department !== this.designation) parts.push(this.department)
      if (this.hasChildren) {
        parts.push(this.collapsed ? 'collapsed, click to show reports' : 'expanded, click to hide reports')
      }
      return parts.join(', ')
    },
  },
  methods: {
    onCardActivate() {
      if (this.hasChildren) {
        this.$emit('toggle', this.node.empIdNorm)
        return
      }
      this.$emit('select', this.node)
    },
  },
}
</script>

<style lang="scss" scoped>
$org-primary: #00ac99;
$org-primary-dark: #007a6e;
$org-info: #00cfe8;
$org-line: rgba(0, 98, 88, 0.88);
$org-line-width: 2.5px;
$org-connector-v: 1.05rem;
$org-heading: #4b465c;
$org-muted: #5e5873;

.org-chart-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 0.18rem;
}

.org-chart-card {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  min-height: 5.25rem;
  padding: 0.75rem 0.65rem 0.75rem 0.9rem;
  background: #fff;
  border: 1px solid #a8a3b3;
  border-radius: 0.5rem;
  box-shadow: none;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease;
  text-align: left;
  position: relative;
  overflow: hidden;
  outline: none;
  z-index: 1;

  &__accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: $org-primary;
    opacity: 0.85;
    transition: opacity 0.18s ease, background-color 0.18s ease;
  }

  &:hover {
    border-color: rgba($org-primary, 0.7);

    .org-chart-card__accent {
      opacity: 1;
    }
  }

  &:focus-visible {
    border-color: $org-primary;
    outline: 2px solid rgba(0, 172, 153, 0.45);
    outline-offset: 1px;
  }

  &--selected {
    border-color: $org-primary;

    .org-chart-card__accent {
      opacity: 1;
      background: $org-primary;
    }
  }

  &--highlight {
    border-color: $org-info;
    animation: org-highlight-pulse 0.9s ease;

    .org-chart-card__accent {
      opacity: 1;
      background: $org-info;
    }
  }

  &--inactive {
    opacity: 0.78;
    background: #f7f7f9;
  }

  &--expanded {
    border-color: $org-primary;
    background: #f4fffc;
    box-shadow: none;
  }

  &__avatar {
    flex-shrink: 0;
    margin-top: 0.05rem;
  }

  &__body {
    flex: 1;
    min-width: 0;
    padding-top: 0.05rem;
  }

  &__name {
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.3;
    color: $org-heading;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  &__designation {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.35;
    margin-top: 0.2rem;
    color: $org-primary-dark;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  &__dept {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.3;
    margin-top: 0.12rem;
    color: $org-muted;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__actions {
    flex-shrink: 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 0.35rem;
  }

  &__info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.6rem;
    height: 1.6rem;
    margin: 0;
    padding: 0;
    border: 1px solid #d8d6de;
    border-radius: 0.35rem;
    background: #fff;
    color: $org-muted;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: rgba($org-primary, 0.55);
      color: $org-primary-dark;
      background: rgba(0, 172, 153, 0.08);
      outline: none;
    }
  }

  &__info-icon {
    display: block;
  }

  &__toggle {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.12rem;
    min-width: 2.1rem;
    padding: 0.3rem 0.28rem;
    margin: 0;
    border: 1.5px solid rgba($org-primary, 0.45);
    border-radius: 0.4rem;
    background: rgba(0, 172, 153, 0.1);
    color: $org-primary-dark;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: rgba($org-primary, 0.75);
      color: $org-primary-dark;
      background: rgba(0, 172, 153, 0.18);
      box-shadow: none;
      outline: none;
    }
  }

  &__chevron {
    display: block;
    stroke-width: 2.5px;
  }

  &__count {
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: $org-primary-dark;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 999px;
    padding: 0.08rem 0.32rem;
    min-width: 1.1rem;
    text-align: center;
  }
}

.org-chart-children {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0;
  position: relative;

  &__connector {
    width: $org-line-width;
    height: $org-connector-v;
    background: $org-line;
    border-radius: 1px;
    flex-shrink: 0;
  }

  &__row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    position: relative;
    padding-top: $org-connector-v;
    gap: 0;
  }

  &__item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 0.12rem;

    /* Vertical stub from horizontal bar down to card */
    &::before {
      content: '';
      position: absolute;
      top: calc(-1 * #{$org-connector-v});
      left: 50%;
      width: $org-line-width;
      height: $org-connector-v;
      background: $org-line;
      transform: translateX(-50%);
      border-radius: 1px;
      z-index: 0;
    }
  }

  /* Horizontal bar via first/middle/last child segments */
  &__row > &__item:first-child:not(:only-child)::after,
  &__row > &__item:last-child:not(:only-child)::after {
    content: '';
    position: absolute;
    top: calc(-1 * #{$org-connector-v});
    height: $org-line-width;
    background: $org-line;
    width: 50%;
    border-radius: 1px;
    z-index: 0;
  }

  &__row > &__item:first-child:not(:only-child)::after {
    left: 50%;
  }

  &__row > &__item:last-child:not(:only-child)::after {
    right: 50%;
  }

  &__row > &__item:not(:first-child):not(:last-child)::after {
    content: '';
    position: absolute;
    top: calc(-1 * #{$org-connector-v});
    left: 0;
    right: 0;
    height: $org-line-width;
    background: $org-line;
    border-radius: 1px;
    z-index: 0;
  }

  /* Junction dot at parent→children fork (multi-child rows only) */
  &__row--multi::before {
    content: '';
    position: absolute;
    top: calc(-1 * #{$org-connector-v});
    left: 50%;
    width: 7px;
    height: 7px;
    background: $org-primary;
    border: 2px solid #fff;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }
}

.org-children-enter-active,
.org-children-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.org-children-enter,
.org-children-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@keyframes org-highlight-pulse {
  0% {
    border-color: rgba(0, 207, 232, 0.4);
  }
  70% {
    border-color: rgba(0, 207, 232, 0.95);
  }
  100% {
    border-color: $org-info;
  }
}
</style>
